// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action } from 'mobx';
import { compose, withProps, withHandlers } from 'recompose';
import ImportStore from 'logic/Izenda.ImportStore';
import { curry } from 'lodash/fp';
import getHistory from '../../../../Izenda.History';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import withNavigator from 'share/components/hoc/Izenda.WithNavigator';

const actions = compose(
  withProps(() => ({
    store: new ImportStore(),
    originalOnPageHide: window.onpagehide
  })),
  withHandlers({
    canChangeTab: curry(canChangeTab)
  }),
  withNavigator({
    onbeforeunload: registerOnBeforeUnloadHandler,
    onpagehide: registerOnPageHideHandler,
    onlistenbefore: registerOnBackHandler,
    onpageshow: registerOnPageShowHandler
  })
);

/**
 * Validate before change tabs.
 * @param {Object} props
 * @param {Object} value
 */
function canChangeTab(props: any, value: any) {
  const { validation, store } = props;

  return validation.validate().then(
    action(status => {
      store.model.activeTabKey === 'report' && (store.hasErrorOnClientSideReportTab = !status);
      store.model.activeTabKey === 'dashboard' && (store.hasErrorOnClientSideDashboardTab = !status);

      if (status) {
        if (store.hasErrorReportTab && store.model.activeTabKey === 'report') {
          validation.updateErrors(store.errorMessages);
          return false;
        }

        if (store.hasErrorDashboardTab && store.model.activeTabKey === 'dashboard') {
          validation.updateErrors(store.errorMessages);
          return false;
        }

        return true;
      }

      validation.updateErrors(store.errorMessages);
      return false;
    })
  );
}

/**
 * Custom onBeforeUnload handler.
 */
function registerOnBeforeUnloadHandler() {
  const { store, localization } = this.props;
  return () => (store.checkDirty() ? localization.getText('IMPORT_CONFORM_REMOVE_ALL_FILE') : null);
}

/**
 * Custom onPageHide handler.
 */
function registerOnPageHideHandler() {
  const { props, props: { store } } = this;
  return () => {
    store.checkDirty() && store.removeFilesSync();
    props.originalOnPageHide && props.originalOnPageHide();
  };
}

/**
 * Register back handler.
 */
function registerOnBackHandler() {
  const { props, props: { store, localization, authorization } } = this;
  const { synergyHistory } = getHistory(authorization.isIntegrationMode());

  return synergyHistory.listenBefore(location => {
    if (location.action === 'POP') {
      if (!store.checkDirty()) return true;
      props
        .confirmDirtyForm(true, {
          customMessage: localization.getText('IMPORT_CONFORM_REMOVE_ALL_FILE'),
          onConfirmDirtyForm: store.onOkDirtyForm.bind(store)
        })
        .then(synergyHistory.goBack);
      return false;
    }
  });
}

/**
 * Register onPageShow handler.
 */
function registerOnPageShowHandler() {
  return event => {
    if (BrowserUtils.isSafariBrowser() && event && event.persisted) {
      window.location.reload();
    }
  };
}

export default actions;



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/Izenda.ImportActions.js